import React from "react";
import {
  Box,
  FormControl,
  IconButton,
  MenuItem,
  Select,
  TextField,
} from "@mui/material";
import { Close as CloseIcon, Save as SaveIcon } from "@mui/icons-material";
import {
  SettingKey,
  SettingValueType,
  ImageSearchSortBehaviour,
} from "@swagger/typescript-fetch-goflyer";
import { TABLE_SETTING_KEYS, TableConfigEditor } from "./TableConfigEditor";

const focusSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "8px",
    "&.Mui-focused fieldset": { borderColor: "#f58220", borderWidth: "2px" },
  },
};

const selectSx = {
  borderRadius: "8px",
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: "#f58220",
    borderWidth: "2px",
  },
};

interface SettingEditFieldProps {
  valueType: SettingValueType | "";
  valueSchema?: string | null;
  settingKey: SettingKey | "";
  editValue: string;
  isSaving: boolean;
  onEditValueChange: (v: string) => void;
  onSave: () => void;
  onCancel: () => void;
  testIdPrefix?: string;
}

export const SettingEditField = ({
  valueType,
  valueSchema,
  settingKey,
  editValue,
  isSaving,
  onEditValueChange,
  onSave,
  onCancel,
  testIdPrefix = "admin-user-settings-edit-field",
}: SettingEditFieldProps) => {
  const renderInput = () => {
    switch (valueType) {
      case SettingValueType.Enum:
        if (valueSchema === "ImageSearchSortBehaviour") {
          return (
            <FormControl size="small" fullWidth>
              <Select
                data-testid={`${testIdPrefix}-input`}
                value={editValue || ""}
                displayEmpty
                onChange={(e) => onEditValueChange(e.target.value)}
                sx={selectSx}
              >
                <MenuItem value="">
                  <em>None</em>
                </MenuItem>
                {[
                  ImageSearchSortBehaviour.UsageCountFirst,
                  ImageSearchSortBehaviour.RelevanceFirst,
                ].map((val) => (
                  <MenuItem key={val} value={val}>
                    {String(val)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          );
        }
        return (
          <TextField
            data-testid={`${testIdPrefix}-input`}
            size="small"
            value={editValue}
            onChange={(e) => onEditValueChange(e.target.value)}
            fullWidth
            sx={focusSx}
          />
        );
      case SettingValueType.Boolean:
        return (
          <FormControl size="small" fullWidth>
            <Select
              data-testid={`${testIdPrefix}-input`}
              value={editValue}
              onChange={(e) => onEditValueChange(e.target.value)}
              sx={selectSx}
            >
              <MenuItem value="true">true</MenuItem>
              <MenuItem value="false">false</MenuItem>
            </Select>
          </FormControl>
        );
      case SettingValueType.Json: {
        const isTableConfig =
          settingKey !== "" && TABLE_SETTING_KEYS.has(settingKey);

        if (isTableConfig) {
          return (
            <TableConfigEditor
              settingKey={settingKey as SettingKey}
              editValue={editValue}
              onEditValueChange={onEditValueChange}
            />
          );
        }
        return (
          <TextField
            data-testid={`${testIdPrefix}-input`}
            size="small"
            value={editValue}
            onChange={(e) => onEditValueChange(e.target.value)}
            fullWidth
            multiline
            rows={3}
            sx={focusSx}
          />
        );
      }
      case SettingValueType.Number:
        return (
          <TextField
            data-testid={`${testIdPrefix}-input`}
            size="small"
            value={editValue}
            onChange={(e) => onEditValueChange(e.target.value)}
            type="number"
            onWheel={(e) => (e.target as HTMLElement).blur()}
            fullWidth
            sx={focusSx}
          />
        );
      default:
        return (
          <TextField
            data-testid={`${testIdPrefix}-input`}
            size="small"
            value={editValue}
            onChange={(e) => onEditValueChange(e.target.value)}
            fullWidth
            sx={focusSx}
          />
        );
    }
  };

  return (
    <Box data-testid={`${testIdPrefix}-container`} sx={{ mt: 1.5, mb: 1, width: "100%" }}>
      <Box sx={{ mb: 1 }}>{renderInput()}</Box>
      <Box sx={{ display: "flex", gap: 1 }}>
        <IconButton
          data-testid={`${testIdPrefix}-save`}
          onClick={onSave}
          disabled={isSaving}
          sx={{
            bgcolor: "#f58220",
            color: "white",
            borderRadius: "8px",
            flexShrink: 0,
            "&:hover": { bgcolor: "#d67320" },
            "&:disabled": { bgcolor: "#e5e7eb" },
          }}
        >
          <SaveIcon />
        </IconButton>
        <IconButton
          data-testid={`${testIdPrefix}-cancel`}
          onClick={onCancel}
          sx={{
            bgcolor: "#f3f4f6",
            color: "#6b7280",
            borderRadius: "8px",
            flexShrink: 0,
            "&:hover": { bgcolor: "#e5e7eb" },
          }}
        >
          <CloseIcon />
        </IconButton>
      </Box>
    </Box>
  );
};
